<template>
  <div class="border border-gray-200 rounded-lg p-3 bg-white">
    <!-- Main row with progress bar -->
    <div class="flex items-center justify-between">
      <div class="w-full">
        <div class="flex items-center">
          <span class="font-medium">{{ title }}</span>
          <i
            v-tooltip="{ content: getPriceTooltip, allowHTML: true }"
            class="far fa-info-circle text-blue-500 ml-1.5 cursor-pointer text-sm"
          ></i>

        </div>
        <div class="flex items-center justify-between gap-4">
         <div class="flex flex-col items-end w-full gap-1">
          <span class="text-sm">{{ displayValue }}</span>
          <Progress
          :value="progressValue"
          :color="progressColor"
          :min-value="usedValue"
          size="xl"
          class="w-full"
          radius="full"
        />
         </div>
           <!-- Add/Remove buttons in a new line -->
        <div v-if="!showAddAction && !showRemoveAction" class="flex items-center justify-end space-x-2">
          <Button
            class="text-nowrap"
            variant="light"
            color="success"
            size="xs"
            @click="$emit('add-action')"
          >
            <i class="far fa-plus text-xs"></i>
            Add
          </Button>
          <div  v-tooltip="getRemoveButtonTooltip">
          <Button
            class="text-nowrap"
            variant="light"
            color="danger"
            size="xs"
            :disabled="isDecreaseDisabled"
            @click="$emit('remove-action')"
          >
            <i class="far fa-minus text-xs"></i>
            Remove
          </Button>
          </div>
        </div>
        </div>

      </div>

      <!-- Price info column -->
      <!-- <div class="text-sm text-right w-[150px]">
        <span>
          {{ dynamicPriceLabel }}<br>
        ${{ price }}/{{ isAnnually ? 'year ' : 'month ' }}</span>
        <img
          v-tooltip="{ content: getPriceTooltip, allowHTML: true }"
          src="@src/assets/img/integration/info_blue.svg"
          alt="Price Details"
          class="h-4 w-4 mb-1 cursor-pointer"
        >
      </div> -->
    </div>

    <!-- Add/Remove action row (conditionally rendered) -->
    <div v-if="showAddAction || showRemoveAction" class="mt-3 pt-3 border-t border-gray-200">
      <!-- Add action UI -->
      <div v-if="showAddAction">
        <div class="flex items-center justify-between mb-2">
          <div class="text-sm font-medium text-green-600">How many {{ formatTitleText(title) }} to add?</div>
          <div class="text-sm cursor-pointer font-medium text-gray-700 hover:text-gray-900" @click="handleCancel">Cancel</div>
        </div>
        <div class="flex flex-wrap items-center">
          <div class="flex items-center">
            <button
              class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200"
              :class="{ 'opacity-50 cursor-not-allowed': isDecreaseDisabled }"
              :disabled="isDecreaseDisabled"
              @click="handleDecrease"
            >
              <i class="far fa-minus text-xs"></i>
            </button>
            <div class="mx-2 w-24" @focusin.stop @mousedown.stop>
              <TextInput
                :model-value="inputValue"
                size="md"
                input-class="text-center !border-0 !shadow-none !m-0"
                type="number"
                :min="0"
                :max="maxValue - props.minValue"
                placeholder="0"
                @change="handleDirectInput"
              />
            </div>
            <button
              class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200"
              :class="{ 'opacity-50 cursor-not-allowed': isIncreaseDisabled }"
              :disabled="isIncreaseDisabled"
              @click="handleIncrease"
            >
              <i class="far fa-plus text-xs"></i>
            </button>
            <div class="ml-3 text-green-600 font-medium whitespace-nowrap">
              + ${{ calculateInputCost(true) }}/{{ isAnnually ? 'year' : 'month' }}
            </div>
          </div>
          <div class="w-full mt-1 text-xs">
            <span
              v-if="showPricingBreakdownTooltip"
              v-tooltip="{ content: getPricingBreakdownTooltip, allowHTML: true }"
              class="text-gray-800 underline"
            >
              View pricing breakdown
            </span>
            <div v-if="props.valueKey === 'aiTextCredits' && showAdjustmentMessage" class="text-blue-600 flex items-center mt-1">
              <i class="fa fa-info-circle mr-1"></i>
              Adjusted to 1,000 increment ({{ inputValue.toLocaleString() }} credits)
            </div>
            <div v-if="props.valueKey === 'aiImageCredits' && showAdjustmentMessage" class="text-blue-600 flex items-center mt-1">
              <i class="fa fa-info-circle mr-1"></i>
              Adjusted to 100 increment ({{ inputValue.toLocaleString() }} credits)
            </div>
          </div>
        </div>
      </div>

      <!-- Remove action UI -->
      <div v-else-if="showRemoveAction">
        <div class="flex items-center justify-between mb-2">
          <div class="text-sm font-medium text-red-600">How many {{ formatTitleText(title) }} to remove?</div>
          <div class="text-sm cursor-pointer font-medium text-gray-700 hover:text-gray-900" @click="handleCancel">Cancel</div>
        </div>
        <div class="flex flex-wrap items-center">
          <div class="flex items-center">
            <button
              class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200"
              :class="{ 'opacity-50 cursor-not-allowed': inputValue.value === 0 }"
              :disabled="inputValue.value === 0"
              @click="handleDecrease"
            >
              <i class="fa fa-minus text-xs"></i>
            </button>
            <div class="mx-2 w-24">
              <TextInput
                :model-value="inputValue"
                size="md"
                input-class="text-center !border-0 !shadow-none !m-0"
                type="number"
                :min="0"
                :max="props.currentValue - props.minValue"
                placeholder="0"
                @change="handleDirectInput"
              />
            </div>
            <button
              class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200"
              :class="{ 'opacity-50 cursor-not-allowed': isIncreaseDisabled }"
              :disabled="isIncreaseDisabled"
              @click="handleIncrease"
            >
              <i class="fa fa-plus text-xs"></i>
            </button>
            <div class="ml-3 text-red-600 font-medium whitespace-nowrap">
              - ${{ calculateInputCost(true) }}/{{ isAnnually ? 'year' : 'month' }}
            </div>
          </div>
          <!-- Error message for remove action when needed -->
          <div v-if="isDecreaseDisabled && inputValue !== 0" class="w-full my-2 text-xs text-red-600 flex items-center">
            <i class="fa fa-exclamation-circle mr-1"></i>
            <span v-if="props.currentValue <= Math.round(props.usedValue)">
              You cannot reduce below current usage ({{ props.usedValue }} {{ formatTitleText(props.title.toLowerCase()) }}).
              <!-- not aitext or image -->
              <span v-if="props.valueKey !== 'aiTextCredits' && props.valueKey !== 'aiImageCredits'">
                Please remove some {{ formatTitleText(props.title.toLowerCase()) }} first
              </span>
               <!-- social account case -->
               <span v-if="props.valueKey === 'socialAccounts' || props.valueKey === 'blogs' || props.valueKey === 'teamMembers'">
                in <span class="text-blue-500 underline cursor-pointer" @click="goToManageLimits">Manage Limits</span>
               </span>
               .
            </span>
            <span v-else-if="props.currentValue <= Math.round(props.minValue)">
              <!-- Media Storage case -->
              <span v-if="props.valueKey === 'mediaStorage'">
                You cannot reduce below your base plan limits ({{ props.minValue }}GB Media Storage).
              </span>
              <span v-else>
                You cannot reduce below your base plan limits ({{ props.minValue }} {{ formatTitleText(props.title) }}).
              </span>
            </span>
          </div>

          <div class="w-full mt-1 text-xs">
            <span
              v-if="showPricingBreakdownTooltip"
              v-tooltip="{ content: getPricingBreakdownTooltip, allowHTML: true }"
              class="text-gray-800 underline"
            >
              View pricing breakdown
            </span>
            <div v-if="props.valueKey === 'aiTextCredits' && showAdjustmentMessage" class="text-blue-600 flex items-center mt-1">
              <i class="fa fa-info-circle mr-1"></i>
              Adjusted to 1,000 increment ({{ inputValue.toLocaleString() }} credits)
            </div>
            <div v-if="props.valueKey === 'aiImageCredits' && showAdjustmentMessage" class="text-blue-600 flex items-center mt-1">
              <i class="fa fa-info-circle mr-1"></i>
              Adjusted to 100 increment ({{ inputValue.toLocaleString() }} credits)
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Progress, TextInput, Button } from '@contentstudio/ui'
import useWorkspace from '@common/composables/useWorkspace'
import { useAdjustLimits } from '@modules/billing/composables/useAdjustLimits'
import router from '@/src/router'

const { addonAdjustments } = useAdjustLimits()

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  limitKey: {
    type: String,
    required: true
  },
  valueKey: {
    type: String,
    required: true
  },
  currentValue: {
    type: Number,
    required: true
  },
  minValue: {
    type: Number,
    required: true
  },
  maxValue: {
    type: Number,
    required: true
  },
  usedValue: {
    type: Number,
    default: 0
  },
  price: {
    type: Number,
    required: true
  },
  priceLabel: {
    type: String,
    required: true
  },
  formatValue: {
    type: Function,
    default: null
  },
  step: {
    type: Number,
    default: 1
  },
  isAnnually: {
    type: Boolean,
    default: false
  },
  addonCount: {
    type: Number,
    default: 0
  },
  isAgencyPlan: {
    type: Boolean,
    default: false
  },
  showAddAction: {
    type: Boolean,
    default: false
  },
  showRemoveAction: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['increase', 'decrease', 'input', 'add-action', 'remove-action', 'cancel-action', 'close'])

const { getUsedLimits } = useWorkspace()

// Define individual prices for each item type
const pricePerItem = computed(() => {
  switch (props.valueKey) {
    case 'aiTextCredits':
      return 0.75 // $0.75 per 1k credits (base tier)
    case 'aiImageCredits':
      return 0.05 // $5 per 100 credits = $0.05 per credit
    case 'socialAccounts':
      if (props.isAgencyPlan) {
        // Will use tiered pricing in calculateInputCost
        return 5 // Default $5 per account
      }
      return 5 // $5 per account
    case 'blogs':
      return 5 // $5 per blog
    case 'mediaStorage':
      return 0.5 // $0.5 per GB
    case 'workspaces':
      return 10 // $10 per workspace
    case 'teamMembers':
      return 10 // $10 per team member
    default:
      return props.price // Fallback to the provided price
  }
})

const isDecreaseDisabled = computed(() => {
  // first need to round the minValue based on the key
  const minVal = Math.max(props.minValue, props.usedValue)

  // For the main remove button, only check if we're at the minimum value
  if (!props.showRemoveAction && !props.showAddAction) {
    return props.currentValue <= Math.round(minVal)
  }

  // For the decrease button in the remove action UI, check if input value is 0
  return props.currentValue <= Math.round(minVal) || inputValue.value === 0
})

const isIncreaseDisabled = computed(() => {
  // In remove mode, disable if at base limit
  if (props.showRemoveAction && props.currentValue <= props.minValue) {
    return true
  }
  // in remove mode, disable if at used value
  if (props.showRemoveAction && props.currentValue <= Math.round(props.usedValue)) {
    return true
  }
  return props.currentValue >= props.maxValue
})



const displayValue = computed(() => {
  const used = props.usedValue || getUsedLimits(props.limitKey)
  // When in add/remove mode, show the potential new value
  let currentVal = props.currentValue

  // Only calculate a new value if we're in add/remove mode AND have an input value
  if ((props.showAddAction || props.showRemoveAction) && inputValue.value > 0) {
    if (props.showAddAction) {
      currentVal = originalCurrentValue.value + inputValue.value
    } else if (props.showRemoveAction) {
      currentVal = originalCurrentValue.value - inputValue.value
    }
  }

  if (props.formatValue) {
    return props.formatValue(used, currentVal)
  }

  return `${used}/${currentVal}`
})

const progressValue = computed(() => {
  const used = props.usedValue || getUsedLimits(props.limitKey)
  // Use the same currentVal calculation as in displayValue
  let total = props.currentValue

  // Only calculate a new total if we're in add/remove mode AND have an input value
  if ((props.showAddAction || props.showRemoveAction) && inputValue.value > 0) {
    if (props.showAddAction) {
      total = originalCurrentValue.value + inputValue.value
    } else if (props.showRemoveAction) {
      total = originalCurrentValue.value - inputValue.value
    }
  }

  return (used / total) * 100
})

const progressColor = computed(() => {
  if (progressValue.value >= 100) {
    return 'danger'
  } else if (progressValue.value >= 80) {
    return 'warning'
  } else {
    return 'primary'
  }
})


// Generate price tooltip based on valueKey
const getPriceTooltip = computed(() => {
  switch (props.valueKey) {
    case 'aiTextCredits':
      return `<p class="text-sm line-clamp-3 text-justify">
        <span class="font-semibold">1-99K credits:</span> $0.75 per 1k credits/month<br>
        <span class="font-semibold">100K-999K credits:</span> $0.50 per 1k credits/month<br>
        <span class="font-semibold">1M+ credits:</span> $0.30 per 1k credits/month
      </p>`

    case 'aiImageCredits':
      return `<p class="text-sm">$5 per 100 credits/month</p>`

    case 'socialAccounts':
      if (props.isAgencyPlan) {
        return `<p class="text-sm">
          <strong>Tiered pricing based on quantity</strong><br>
          <strong>Pricing Tiers:</strong><br>
          First 25 accounts: Included in plan<br>
          Next 25 accounts: $5 per account<br>
          Next 50 accounts: $4 per account<br>
          Next 100 accounts: $3 per account<br>
          Next 300 accounts: $2 per account<br>
          Additional accounts: $1 per account
        </p>
        `
      }
      return `<p class="text-sm">$5 per extra social account/month</p>`

    case 'blogs':
      return `<p class="text-sm">$5 per extra blog/month</p>`

    case 'mediaStorage':
      return `<p class="text-sm">$0.5 per extra GB/month</p>`

    case 'workspaces':
      return `<p class="text-sm">$10 per extra workspace/month</p>`

    case 'teamMembers':
      return `<p class="text-sm">$10 per extra user/month</p>`

    default:
      return ''
  }
})

const handleIncrease = () => {
  if (!isIncreaseDisabled.value) {
    // Determine increment amount based on valueKey
    let incrementAmount = 1;
    if (props.valueKey === 'aiTextCredits') {
      incrementAmount = 1000; // Increment by 1000 for text credits
    } else if (props.valueKey === 'aiImageCredits') {
      incrementAmount = 100; // Increment by 100 for image credits
    }

    inputValue.value = (inputValue.value || 0) + incrementAmount

    // Calculate the new total value
    let newTotal = props.currentValue
    if (props.showAddAction) {
      // For add action, calculate based on the input value
      newTotal = originalCurrentValue.value + inputValue.value
    } else if (props.showRemoveAction) {
      // For remove action, calculate based on the input value
      newTotal = originalCurrentValue.value - inputValue.value
    }

    emit('input', newTotal)
    // Update addon adjustments
    addonAdjustments.value = {
      ...addonAdjustments.value,
      [props.valueKey]: {
        name: props.title || props.limitKey || props.valueKey,
        itemCount: inputValue.value,
        operation: props.showAddAction ? 'add' : 'remove',
        pricePerItem: parseFloat(calculateInputCost()) / inputValue.value // Calculate price per item
      }
    }
  }
}

const handleDecrease = () => {
  // In remove action UI, only check if input value is greater than 0
  if ((props.showRemoveAction || !isDecreaseDisabled.value) && inputValue.value > 0) {
    // Determine decrement amount based on valueKey
    let decrementAmount = 1;
    if (props.valueKey === 'aiTextCredits') {
      decrementAmount = 1000; // Decrement by 1000 for text credits
    } else if (props.valueKey === 'aiImageCredits') {
      decrementAmount = 100; // Decrement by 100 for image credits
    }

    // Ensure we don't go below 0
    if (inputValue.value >= decrementAmount) {
      inputValue.value = inputValue.value - decrementAmount
    } else {
      inputValue.value = 0
    }

    // Calculate the new total value
    let newTotal = props.currentValue
    if (props.showAddAction) {
      // For add action, calculate based on the input value
      newTotal = originalCurrentValue.value + inputValue.value
    } else if (props.showRemoveAction) {
      // For remove action, calculate based on the input value
      newTotal = originalCurrentValue.value - inputValue.value
    }

    emit('input', newTotal)
    // Update addon adjustments
    if (inputValue.value > 0) {
    addonAdjustments.value = {
      ...addonAdjustments.value,
      [props.valueKey]: {
        name: props.title || props.limitKey || props.valueKey,
        itemCount: inputValue.value,
        operation: props.showAddAction ? 'add' : 'remove',
        pricePerItem: parseFloat(calculateInputCost()) / inputValue.value // Calculate price per item
      }
    }
    }else{
      // If no items are being added/removed, remove this item from addonAdjustments
      if (addonAdjustments.value[props.valueKey]) {
        const newAdjustments = { ...addonAdjustments.value }
        delete newAdjustments[props.valueKey]
        addonAdjustments.value = newAdjustments
      }
    }
  }
}

const showPricingBreakdownTooltip = computed(() => {
  return props.isAgencyPlan && ( props.valueKey === 'socialAccounts' || props.valueKey === 'aiTextCredits' ) && inputValue.value > 0
})

// Calculate the cost based on the input value
const calculateInputCost = (applyAnnualMultiplier = false) => {
  const value = inputValue.value || 0
  const annualMultiplier = (props.isAnnually && applyAnnualMultiplier) ? 12 : 1

  // If no value is entered, return 0
  if (value === 0) {
    return '0.00';
  }

  // For social accounts with tiered pricing
  if (props.valueKey === 'socialAccounts' && props.isAgencyPlan) {
    let totalCost = 0;
    // Use originalCurrentValue to ensure we're calculating from the correct starting point
    const currentCount = originalCurrentValue.value;
    const targetCount = props.showAddAction ? currentCount + value : currentCount - value;

    // Calculate cost based on tiers
    const startIndex = Math.min(currentCount, targetCount) + 1;
    const endIndex = Math.max(currentCount, targetCount);

    for (let i = startIndex; i <= endIndex; i++) {
      if (i >= 25 && i <= 50) totalCost += 5;
      else if (i >= 51 && i <= 100) totalCost += 4;
      else if (i >= 101 && i <= 200) totalCost += 3;
      else if (i >= 201 && i <= 500) totalCost += 2;
      else if (i >= 501) totalCost += 1;
      else totalCost += 5; // Default price for accounts under 25
    }

    return (totalCost * annualMultiplier).toFixed(2);
  }

  // Handle special cases for AI credits
  if (props.valueKey === 'aiTextCredits') {
    // $0.75 per 1k credits (base tier)
    return ((value / 1000) * 0.75 * annualMultiplier).toFixed(2);
  } else if (props.valueKey === 'aiImageCredits') {
    // $5 per 100 credits
    return ((value / 100) * 5 * annualMultiplier).toFixed(2);
  }

  // For all other items, use the individual price per item
  return (value * pricePerItem.value * annualMultiplier).toFixed(2);
}

// Add ref for input value
const inputValue = ref(0)

// Track if we need to show adjustment message
const showAdjustmentMessage = ref(false)

// Store the original current value when entering add/remove mode
const originalCurrentValue = ref(0)

// This computed property is no longer used since we now display actual values
// Keeping it for potential future use or reference

// Reset input value and store original value when action changes
watch(() => [props.showAddAction, props.showRemoveAction], () => {
  inputValue.value = 0
  showAdjustmentMessage.value = false

  // Store the original current value when entering add/remove mode
  if (props.showAddAction || props.showRemoveAction) {
    originalCurrentValue.value = props.currentValue
  }
})

// Handle cancel button click
const handleCancel = () => {
  // Reset input value immediately to update the display
  inputValue.value = 0

  // Emit cancel event
  emit('cancel-action', originalCurrentValue.value)

  // Restore the original value when canceling
  // emit('input', originalCurrentValue.value)

  // Clear addon adjustments
  addonAdjustments.value = {
    ...addonAdjustments.value,
    [props.valueKey]: null
  }
}

// Generate pricing breakdown tooltip based on valueKey
const getPricingBreakdownTooltip = computed(() => {
  if (props.valueKey === 'socialAccounts') {
    let html = `<div class="p-2">
      <div class="font-medium mb-1">Price Breakdown</div>
      <div>Base plan includes ${props.minValue} accounts</div>`;

    if (props.isAgencyPlan) {
      // Only show relevant tiers based on current value or target value
      const maxValue = props.showAddAction ?
        Math.max(props.currentValue + (inputValue.value || 0), 25) :
        Math.max(props.currentValue, 25);

      if (maxValue >= 25) {
        html += `<div>25-50 accounts at $5/account</div>`;
      }
      if (maxValue >= 51) {
        html += `<div>51-100 accounts at $4/account</div>`;
      }
      if (maxValue >= 101) {
        html += `<div>101-200 accounts at $3/account</div>`;
      }
      if (maxValue >= 201) {
        html += `<div>201-500 accounts at $2/account</div>`;
      }
      if (maxValue >= 501) {
        html += `<div>501+ accounts at $1/account</div>`;
      }
    } else {
      html += `<div>Additional accounts at $5/account</div>`;
    }

    html += '</div>';
    return html;
  }
  // AI Text Credits
  if (props.valueKey === 'aiTextCredits') {
    let html = '';
    // get the credits value
    const creditsValue = props.currentValue;
    if(creditsValue >= 1000){
      html += `<p class="text-sm line-clamp-3 text-justify">
        <span class="font-semibold">1-99K credits:</span> $0.75 per 1k credits/month
      </p>`;
    }
    if(creditsValue >= 100000){
      html += `<p class="text-sm line-clamp-3 text-justify">
        <span class="font-semibold">100K-999K credits:</span> $0.50 per 1k credits/month
      </p>`;
    }
    if(creditsValue >= 1000000){
      html += `<p class="text-sm line-clamp-3 text-justify">
        <span class="font-semibold">1M+ credits:</span> $0.30 per 1k credits/month
      </p>`;
    }

    return html;
  }
  return '';
})

// Generate tooltip for remove button
const getRemoveButtonTooltip = computed(() => {
  if (isDecreaseDisabled.value) {
    if (props.currentValue <= Math.round(props.usedValue)) {
      return `Cannot reduce below current usage (${props.usedValue} ${props.title}). ${props.valueKey !== 'aiTextCredits' && props.valueKey !== 'aiImageCredits' && props.valueKey !== 'mediaStorage' ? 'Please remove some ' + props.title + ' first' : ''}`;
    } else if (props.currentValue <= Math.round(props.minValue)) {
      if(props.valueKey === 'mediaStorage'){
        return ` You cannot reduce below your base plan limits (${props.minValue}GB Media Storage).`;
      }
      return ` You cannot reduce below your base plan limits (${props.minValue} ${props.title}).`;
    }
    return 'Cannot remove';
  }
  return '';
})

// Handle direct input (now showing actual values)
const handleDirectInput = (value) => {
  // Reset adjustment message flag
  showAdjustmentMessage.value = false

  // Check if the input is a positive number
  if (!/^\d+$/.test(value.target.value)) {
    // If not a positive number, revert to input value
    value.target.value = inputValue.value || 0
    return
  }

  // Convert input to number
  let numValue = Number(value.target.value)

  // Handle non-numeric input
  if (isNaN(numValue)) {
    numValue = 0
  }

  // Ensure value is at least 0
  numValue = Math.max(0, numValue)

  // Handle special cases for AI credits
  if (props.valueKey === 'aiTextCredits') {
    // Ensure value is a multiple of 1000
    if (numValue % 1000 !== 0) {
      // Find the closest multiple of 1000
      const remainder = numValue % 1000
      numValue = remainder < 500 ? numValue - remainder : numValue + (1000 - remainder)
      showAdjustmentMessage.value = true
    }
  } else if (props.valueKey === 'aiImageCredits') {
    // Ensure value is a multiple of 100
    if (numValue % 100 !== 0) {
      // Find the closest multiple of 100
      const remainder = numValue % 100
      numValue = remainder < 50 ? numValue - remainder : numValue + (100 - remainder)
      showAdjustmentMessage.value = true
    }
  }

  // Apply limits based on action type
  if (props.showAddAction) {
    // For add action, ensure it doesn't exceed maximum allowed
    numValue = Math.min(numValue, props.maxValue - originalCurrentValue.value)
  } else if (props.showRemoveAction) {
    // For remove action, ensure it doesn't exceed current addon count
    // This is the maximum number of items that can be removed (current - minimum)
    // User cannot remove more than used count
    const usedCount = Math.round(props.usedValue)
    const maxRemovable = originalCurrentValue.value - usedCount
    numValue = Math.min(numValue, maxRemovable)

    // Don't allow removing if we're at the base limit
    if (originalCurrentValue.value <= usedCount) {
      numValue = 0
    }
  }

  // Update the input value
  inputValue.value = numValue

  // Calculate the new total value
  let newTotal = props.currentValue
  if (props.showAddAction) {
    // For add action, add the input value to the current value
    newTotal = originalCurrentValue.value + numValue
  } else if (props.showRemoveAction) {
    // For remove action, subtract the input value from the original value
    // This prevents cumulative subtraction when changing the input value multiple times
    newTotal = originalCurrentValue.value - numValue
  }

  // Emit the new total value
  emit('input', newTotal)

  // Update addon adjustments
  if (numValue > 0) {
    addonAdjustments.value = {
      ...addonAdjustments.value,
      [props.valueKey]: {
        name: props.title || props.limitKey || props.valueKey,
        itemCount: numValue,
        operation: props.showAddAction ? 'add' : 'remove',
        pricePerItem: parseFloat(calculateInputCost(false)) / numValue, // Calculate price per item (monthly price)
        isAnnually: props.isAnnually
      }
    }
  } else {
    // If no items are being added/removed, remove this item from addonAdjustments
    if (addonAdjustments.value[props.valueKey]) {
      const newAdjustments = { ...addonAdjustments.value }
      delete newAdjustments[props.valueKey]
      addonAdjustments.value = newAdjustments
    }
  }

  // Show adjustment message for 5 seconds then hide it
  if (showAdjustmentMessage.value) {
    setTimeout(() => {
      showAdjustmentMessage.value = false
    }, 5000)
  }
}

const goToManageLimits = () => {
  router.push({ name: 'manageLimits' })

  // hide the modal
  emit('close')
}

// Format title text - convert 'gb' to 'GB' in the title
const formatTitleText = (text) => {
  if (!text) return ''
  // Convert the text to lowercase first
  const lowerText = text.toLowerCase()
  // Check if the text contains 'gb' and replace it with 'GB'
  if (lowerText.includes('gb')) {
    return lowerText.replace(/gb/gi, 'GB')
  }
  // Check if the text contains 'ai' and replace it with 'AI'
  if (lowerText.includes('ai')) {
    return lowerText.replace(/ai/gi, 'AI')
  }

  // Otherwise just return the lowercase text
  return lowerText
}

// handleInput function removed - we now use handleDirectInput instead
</script>
